import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { AuditPage, AuditSummary } from '@nest-recruit/shared';
import { ApiError, apiGet, apiUrl, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import {
  feedSearchOf,
  filtersOf,
  summarySearchOf,
  type AccessLogSearch,
  type AuditFilters,
} from '../components/access-log/access-log-address';

// USER-06, the access log (Admin redesign 2026-11-02, Access log A). The feed pages by the server's cursor, the
// kinds are counted for the same filters, and the export is what the filters show.

export const AUDIT_KEY = ['audit'] as const;
const PAGE_SIZE = 50;

/** Where the next page starts: the cursor, and the window the first page was read for, so every page agrees. */
export interface AuditPageParam {
  readonly cursor: string;
  readonly from: string;
  readonly to: string | null;
}

/** The query for one page. A later page reuses the first page's window, so "last 30 days" can't drift while scrolling. */
export function auditPageQuery(filters: AuditFilters, page: AuditPageParam | null): Record<string, string | number | undefined> {
  return {
    ...filters,
    ...(page ? { cursor: page.cursor, from: page.from, ...(page.to ? { to: page.to } : {}) } : {}),
    limit: PAGE_SIZE,
  };
}

/** The next page's start, or undefined on the last page. */
export function nextPageParamOf(page: AuditPage): AuditPageParam | undefined {
  return page.nextCursor ? { cursor: page.nextCursor, from: page.from, to: page.to } : undefined;
}

/** The day feed, newest first. A filter change swaps rows in place, never back to a skeleton (06-qol §5). */
export function useAuditFeed(search: AccessLogSearch, isEnabled = true) {
  const feedSearch = feedSearchOf(search);
  return useInfiniteQuery<AuditPage, ApiError, InfiniteData<AuditPage, AuditPageParam | null>, readonly unknown[], AuditPageParam | null>({
    queryKey: [...AUDIT_KEY, 'feed', feedSearch],
    enabled: isEnabled,
    initialPageParam: null,
    queryFn: ({ pageParam }) => apiGet<AuditPage>('/audit', { query: auditPageQuery(filtersOf(feedSearch), pageParam) }),
    getNextPageParam: nextPageParamOf,
    placeholderData: keepPreviousData,
  });
}

/** The count per kind, for the same Who, When and search. Picking a kind does not change them. */
export function useAuditSummary(search: AccessLogSearch, isEnabled = true) {
  const summarySearch = summarySearchOf(search);
  return useQuery<AuditSummary, ApiError>({
    queryKey: [...AUDIT_KEY, 'summary', summarySearch],
    enabled: isEnabled,
    queryFn: () => apiGet<AuditSummary>('/audit/summary', { query: { ...filtersOf(summarySearch) } }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** `GET /audit/export` for what the filters show. */
export function auditExportUrl(search: AccessLogSearch, now: Date = new Date()): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filtersOf(feedSearchOf(search), now))) {
    if (value !== undefined && value !== '') params.set(key, value);
  }
  const query = params.toString();
  return apiUrl(`/audit/export${query === '' ? '' : `?${query}`}`);
}

function messageOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null || !('error' in payload)) return null;
  const error = payload.error;
  if (typeof error !== 'object' || error === null || !('message' in error)) return null;
  return typeof error.message === 'string' ? error.message : null;
}

/**
 * Fetched rather than opened as a link, so a refusal (422: more than 10,000 entries) shows the server's own
 * message instead of a page of raw error text. Resolves once the browser has been handed the file.
 */
export async function downloadAuditExport(search: AccessLogSearch): Promise<void> {
  let response: Response;
  try {
    response = await fetch(auditExportUrl(search), { credentials: 'same-origin' });
  } catch {
    throw new ApiError({ code: 'network_unreachable', message: "Couldn't reach the server. Check your connection and try again.", status: 0 });
  }
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError({ code: 'export_failed', message: messageOf(payload) ?? "Couldn't make the file. Try again.", status: response.status });
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = 'access-log.csv';
  link.click();
  URL.revokeObjectURL(url);
}

const TOAST_KEY = 'access-log-export';

/** "Export": the button greys while the file is made, and a toast says how it went. A click never waits. */
export function useAuditExport(): { readonly isPreparing: boolean; readonly run: (search: AccessLogSearch) => void } {
  const [isPreparing, setPreparing] = useState(false);
  const showToast = useToasts((state) => state.show);
  const run = useCallback(
    (search: AccessLogSearch) => {
      if (isPreparing) return;
      setPreparing(true);
      showToast({ key: TOAST_KEY, tone: 'info', message: 'Preparing the file…', detail: 'What the filters show. You can keep working.' });
      downloadAuditExport(search)
        .then(() => showToast({ key: TOAST_KEY, tone: 'success', message: 'File ready', detail: 'Your browser saves access-log.csv.' }))
        .catch((error: unknown) => showToast({ key: TOAST_KEY, tone: 'danger', message: "The file wasn't made", detail: errorMessage(error) }))
        .finally(() => setPreparing(false));
    },
    [isPreparing, showToast],
  );
  return { isPreparing, run };
}
